/* eslint-disable max-lines -- This route intentionally composes the complete PO workspace. */
import { useMutation, useQuery } from '@apollo/client/react';
import {
  buttonVariants,
  DimeAlert,
  DimeAlertDescription,
  DimeAlertTitle,
  DimeButton,
  DimeCard,
  DimeCardAction,
  DimeCardDescription,
  DimeCardHeader,
  DimeCardTitle,
  DimeDataTable,
  type RowSelectionState,
  type SortingState,
} from '@nudool/design-system';
import type { PurchaseOrderStatus } from '@nudool/shared';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { ShoppingCart } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ListEmptyState } from '../../components/ListEmptyState.js';
import { ListErrorState } from '../../components/ListErrorState.js';
import type {
  IssuePurchaseOrderMutation,
  IssuePurchaseOrderMutationVariables,
  PurchaseOrderWhere,
} from '../../generated/graphql/graphql.js';
import { useBusinessDayNow } from '../../hooks/use-business-day-now.js';
import { buildOrderBy } from '../../hooks/use-list-table-helpers/index.js';
import { useListTable } from '../../hooks/use-list-table.js';
import { NO_REFETCH } from '../../lib/no-refetch.js';
import { PermissionGuard } from '../../lib/permission-guard.js';
import { toastEntityUpdated } from '../../lib/toast-entity-updated.js';
import { usePermissions } from '../../modules/iam/permissions-context.js';
import {
  GET_BRAND_OPTIONS,
  type GetBrandOptionsQueryResult,
} from '../../modules/products/operations.js';
import {
  issuePurchaseOrdersSequentially,
  partitionSelectedPurchaseOrdersForBulkIssue,
} from '../../modules/purchasing/purchase-order-bulk-issue.js';
import { makePurchaseOrderColumns } from '../../modules/purchasing/purchase-order-columns.js';
import type {
  UpdatePurchaseOrderMutationResult,
  UpdatePurchaseOrderMutationVariables,
} from '../../modules/purchasing/purchase-order-detail-operations.js';
import {
  GET_PURCHASE_ORDER_WAREHOUSE_OPTIONS,
  statesFromWarehouses,
  warehouseStateRefs,
  type GetPurchaseOrderWarehouseOptionsQueryResult,
} from '../../modules/purchasing/purchase-order-filter-options.js';
import {
  buildPurchaseOrderRefinementWhere,
  countPurchaseOrderFilters,
  emptyPurchaseOrderListFilters,
  mergePurchaseOrderWhere,
  type PurchaseOrderListFilters,
} from '../../modules/purchasing/purchase-order-list-filters.js';
import {
  GET_PURCHASE_ORDERS,
  GET_PURCHASE_ORDER_METRICS,
  ISSUE_PURCHASE_ORDER,
  purchaseOrdersFromQuery,
  purchaseOrderMetricsFromQuery,
  UPDATE_PURCHASE_ORDER,
  type GetPurchaseOrderMetricsQueryResult,
  type GetPurchaseOrderMetricsQueryVariables,
  type GetPurchaseOrdersQueryResult,
  type GetPurchaseOrdersQueryVariables,
  type PurchaseOrderListItem,
  type PurchaseOrderMetrics,
} from '../../modules/purchasing/purchase-order-list-operations.js';
import {
  buildPurchaseOrderListWhere,
  parsePurchaseOrderIdsParam,
  parsePurchaseOrderWorkspaceSearch,
  parseWorkspaceQueueParam,
} from '../../modules/purchasing/purchase-order-list-utils.js';
import { PurchaseOrderMetricsBar } from '../../modules/purchasing/purchase-order-metrics-bar.js';
import { purchaseOrderQueueAdmitsStatus } from '../../modules/purchasing/purchase-order-queue-admits-status.js';
import { PurchaseOrderToolbarFilters } from '../../modules/purchasing/purchase-order-toolbar-filters.js';
import {
  PURCHASE_ORDER_WORKSPACE_QUEUES,
  type PurchaseOrderWorkspaceQueue,
} from '../../modules/purchasing/purchase-order-workspace-queues.js';
import {
  GET_VENDOR_CHIP,
  GET_VENDOR_OPTIONS,
  type GetVendorChipQueryResult,
  type GetVendorChipQueryVariables,
  type GetVendorOptionsQueryResult,
} from '../../modules/vendors/operations.js';

const PO_SEARCH_PLACEHOLDER = 'Search by PO #…';
const EMPTY_METRICS: PurchaseOrderMetrics = {
  lateCount: 0,
  lateValue: 0,
  draftCount: 0,
  issuedCount: 0,
  shippedCount: 0,
  partiallyReceivedCount: 0,
  receivedCount: 0,
  cancelledCount: 0,
  dueThisWeekCount: 0,
  openCount: 0,
  openValue: 0,
  landingThisWeekValue: 0,
  vendorOnTimePct: null,
  shortReceivedCount: null,
};

// Verified against SORTABLE_FIELDS.PurchaseOrder; computed columns and `buyer` have no sort path.
export const PURCHASE_ORDER_SORT_MAP: Record<string, string[]> = {
  num: ['num'],
  vendor: ['vendor', 'name'],
  status: ['statusId'],
  dateFirstShip: ['dateFirstShip'],
};
const PURCHASE_ORDER_DEFAULT_SORT: SortingState = [{ id: 'dateFirstShip', desc: false }];
// PurchaseOrderOrderBy doesn't expose `id`, so the default tiebreaker would be rejected.
const PURCHASE_ORDER_SORT_TIEBREAKER_FIELD = 'num';
const RECENT_PURCHASE_ORDERS_ORDER_BY = buildOrderBy({
  active: PURCHASE_ORDER_DEFAULT_SORT[0],
  sortMap: PURCHASE_ORDER_SORT_MAP,
  defaultSort: PURCHASE_ORDER_DEFAULT_SORT,
  tiebreakerField: PURCHASE_ORDER_SORT_TIEBREAKER_FIELD,
}) as GetPurchaseOrdersQueryVariables['orderBy'];

type ListAlert = { title: string; description: string };

const LIST_NOT_REFRESHED_TITLE = 'List not refreshed';
const BULK_ISSUE_FAILED_TITLE = 'Some purchase orders could not be issued';

type PurchaseOrderRouteSearch = {
  queue?: PurchaseOrderWorkspaceQueue;
  ids?: string;
  vendorId?: string;
  size?: number;
  sort?: string;
  q?: string;
};

export const Route = createFileRoute('/purchase-orders/')({
  validateSearch: (search: Record<string, unknown>): PurchaseOrderRouteSearch =>
    parsePurchaseOrderWorkspaceSearch(search),
  component: PurchaseOrdersIndexPage,
});

// eslint-disable-next-line react-refresh/only-export-components
function PurchaseOrdersIndexPage() {
  const navigate = useNavigate({ from: '/purchase-orders/' });
  const { permissions } = usePermissions();
  const canEdit = permissions.includes('purchaseOrder:edit');
  const routeSearch = Route.useSearch();
  const { ids, vendorId } = routeSearch;
  const queue = parseWorkspaceQueueParam(routeSearch.queue);
  const recentIds = useMemo(() => parsePurchaseOrderIdsParam(ids), [ids]);
  const isRecentFilterActive = recentIds.length > 0;
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [bulkIssueError, setBulkIssueError] = useState<ListAlert | null>(null);
  const [bulkIssuing, setBulkIssuing] = useState(false);
  const [savingExpectedDeliveryId, setSavingExpectedDeliveryId] = useState<string | null>(null);
  const [expectedDeliveryError, setExpectedDeliveryError] = useState<ListAlert | null>(null);
  const [poFilters, setPoFilters] = useState<PurchaseOrderListFilters>(
    emptyPurchaseOrderListFilters,
  );

  const [issuePurchaseOrder] = useMutation<
    IssuePurchaseOrderMutation,
    IssuePurchaseOrderMutationVariables
  >(ISSUE_PURCHASE_ORDER, NO_REFETCH);

  const [updatePurchaseOrder] = useMutation<
    UpdatePurchaseOrderMutationResult,
    UpdatePurchaseOrderMutationVariables
  >(UPDATE_PURCHASE_ORDER, NO_REFETCH);

  useEffect(() => {
    const focusSearch = (event: KeyboardEvent) => {
      if (event.key !== '/' || event.altKey || event.ctrlKey || event.metaKey) return;
      const target = event.target;
      if (
        target instanceof HTMLElement &&
        (target.isContentEditable ||
          target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT')
      ) {
        return;
      }

      const searchInput = document.querySelector<HTMLInputElement>(
        `input[aria-label="${PO_SEARCH_PLACEHOLDER}"]`,
      );
      if (searchInput) {
        event.preventDefault();
        searchInput.focus();
      }
    };

    document.addEventListener('keydown', focusSearch);
    return () => document.removeEventListener('keydown', focusSearch);
  }, []);

  const [vendorSearch, setVendorSearch] = useState('');
  const {
    data: vendorsData,
    loading: vendorsLoading,
    error: vendorsError,
  } = useQuery<GetVendorOptionsQueryResult>(GET_VENDOR_OPTIONS, {
    variables: { first: 50, search: vendorSearch || undefined },
  });
  const { data: selectedVendorData } = useQuery<
    GetVendorChipQueryResult,
    GetVendorChipQueryVariables
  >(GET_VENDOR_CHIP, { variables: { id: vendorId ?? '' }, skip: !vendorId });

  const { data: brandsData } = useQuery<GetBrandOptionsQueryResult>(GET_BRAND_OPTIONS, {
    variables: { first: 100 },
  });
  // Warehouses carry the address the State filter resolves through; a permission gap should
  // leave the filter empty rather than blanking the page.
  const { data: warehousesData } = useQuery<GetPurchaseOrderWarehouseOptionsQueryResult>(
    GET_PURCHASE_ORDER_WAREHOUSE_OPTIONS,
    {
      variables: { first: 100 },
      errorPolicy: 'all',
    },
  );

  const {
    data: metricsData,
    loading: metricsLoading,
    error: metricsError,
    refetch: refetchMetrics,
  } = useQuery<GetPurchaseOrderMetricsQueryResult, GetPurchaseOrderMetricsQueryVariables>(
    GET_PURCHASE_ORDER_METRICS,
    {
      variables: { vendorId },
    },
  );

  const brands = useMemo(
    () => brandsData?.brands.edges.map((edge) => edge.node) ?? [],
    [brandsData],
  );
  const warehouses = useMemo(
    () => warehousesData?.locationGroups.edges.map((edge) => edge.node) ?? [],
    [warehousesData],
  );
  const states = useMemo(() => statesFromWarehouses(warehouses), [warehouses]);

  // Held still across renders (fresh Date each render → Apollo variable churn → refetch storm →
  // nginx 429), moved forward when the business day changes so late / dueThisWeek keep agreeing
  // with the metrics rail, and re-anchored on an explicit navigation (see navigateWorkspaceSearch).
  const [listNow, reanchorListNow] = useBusinessDayNow();

  // recentIds stays `[]` here because that view runs as its own query below, outside useListTable.
  const { where: queueWhere, hasOpenLines: listHasOpenLines } = useMemo(
    () =>
      buildPurchaseOrderListWhere({
        queue,
        recentIds: [],
        vendorId,
        now: listNow,
      }),
    [queue, vendorId, listNow],
  );
  const listWhere = useMemo(
    () =>
      mergePurchaseOrderWhere(
        queueWhere,
        buildPurchaseOrderRefinementWhere(poFilters, warehouseStateRefs(warehouses)),
      ),
    [queueWhere, poFilters, warehouses],
  );

  const {
    rows: listRows,
    loading: listLoading,
    error: listError,
    refetch: listRefetch,
    tableProps,
    search,
  } = useListTable<
    GetPurchaseOrdersQueryResult,
    GetPurchaseOrdersQueryVariables,
    PurchaseOrderListItem,
    PurchaseOrderWhere
  >({
    query: GET_PURCHASE_ORDERS,
    extractConnection: (data) => data?.purchaseOrders,
    defaultSort: PURCHASE_ORDER_DEFAULT_SORT,
    sortMap: PURCHASE_ORDER_SORT_MAP,
    sortTiebreakerField: PURCHASE_ORDER_SORT_TIEBREAKER_FIELD,
    where: listWhere,
    variables: { hasOpenLines: listHasOpenLines },
    // The recentIds query below drives the table in that mode, so skip this unused request.
    skip: isRecentFilterActive,
  });

  // This ad-hoc query must pass its own orderBy, or it falls back to the server's default order.
  const {
    data: recentData,
    loading: recentLoading,
    error: recentError,
    refetch: recentRefetch,
  } = useQuery<GetPurchaseOrdersQueryResult, GetPurchaseOrdersQueryVariables>(GET_PURCHASE_ORDERS, {
    variables: {
      first: recentIds.length,
      where: { id: { in: recentIds } },
      orderBy: RECENT_PURCHASE_ORDERS_ORDER_BY,
    },
    skip: !isRecentFilterActive,
  });

  const loading = isRecentFilterActive ? recentLoading : listLoading;
  const error = isRecentFilterActive ? recentError : listError;
  const refetch = isRecentFilterActive ? recentRefetch : listRefetch;

  const handleExpectedDeliverySave = useCallback(
    async (id: string, dateFirstShip: string | null) => {
      setSavingExpectedDeliveryId(id);
      setExpectedDeliveryError(null);
      try {
        const result = await updatePurchaseOrder({
          variables: {
            id,
            input: { dateFirstShip },
            canViewLocationGroups: permissions.includes('locationGroup:view'),
          },
        });
        const num = result.data?.updatePurchaseOrder.num ?? 'Purchase order';
        toastEntityUpdated(num, 'updated');
        setRowSelection({});
        // The write already succeeded above; a refetch failure here must not be reported as
        // an update failure (see the analogous split in handleBulkIssue's refetch handling).
        try {
          await Promise.all([refetch(), refetchMetrics()]);
        } catch {
          setExpectedDeliveryError({
            title: LIST_NOT_REFRESHED_TITLE,
            description: `${num} was updated, but the list could not be refreshed. Reload to see the latest state.`,
          });
        }
      } catch {
        setExpectedDeliveryError({
          title: 'Expected delivery not updated',
          description: 'Failed to update expected delivery.',
        });
      } finally {
        setSavingExpectedDeliveryId(null);
      }
    },
    [permissions, refetch, refetchMetrics, updatePurchaseOrder],
  );

  const columns = useMemo(
    () =>
      makePurchaseOrderColumns({
        canEdit,
        onSave: handleExpectedDeliverySave,
        savingId: savingExpectedDeliveryId,
      }),
    [canEdit, handleExpectedDeliverySave, savingExpectedDeliveryId],
  );

  const purchaseOrders = isRecentFilterActive ? purchaseOrdersFromQuery(recentData) : listRows;
  const { draftIds } = useMemo(
    () => partitionSelectedPurchaseOrdersForBulkIssue(purchaseOrders, rowSelection),
    [purchaseOrders, rowSelection],
  );
  const showBulkIssueButton = canEdit && draftIds.length > 0;
  const metrics = purchaseOrderMetricsFromQuery(metricsData) ?? EMPTY_METRICS;
  const vendors = useMemo(() => {
    const searched = vendorsData?.vendors.edges.map((edge) => edge.node) ?? [];
    const selected = selectedVendorData?.vendor;
    if (selected && !searched.some((vendor) => vendor.id === selected.id)) {
      return [selected, ...searched];
    }
    return searched;
  }, [vendorsData, selectedVendorData]);
  // parseWorkspaceQueueParam guarantees queue is a valid member; find always returns a value.
  const queueMetadata = PURCHASE_ORDER_WORKSPACE_QUEUES.find((item) => item.id === queue)!;

  const applyPoFilters = useCallback((next: PurchaseOrderListFilters) => {
    setRowSelection({});
    setBulkIssueError(null);
    setPoFilters(next);
  }, []);

  const createPoAction = (
    <PermissionGuard
      required={['purchaseOrder:edit']}
      pass={
        <Link to="/purchase-orders/new" className={buttonVariants({})}>
          + Create PO
        </Link>
      }
      fail={
        <span className={buttonVariants({})} role="link" aria-disabled="true">
          + Create PO
        </span>
      }
    />
  );

  /*
   * Only an unfiltered queue gets the illustrated empty state. It replaces the table, and the
   * filters live in the table's toolbar — so showing it while a filter is engaged removes the
   * only control that could undo the filter, stranding you on an empty page.
   */
  const showEmptyState =
    !loading &&
    !error &&
    purchaseOrders.length === 0 &&
    !search &&
    !vendorId &&
    countPurchaseOrderFilters(poFilters) === 0 &&
    !isRecentFilterActive;

  const navigateWorkspaceSearch = (
    patch: (previous: PurchaseOrderRouteSearch) => PurchaseOrderRouteSearch,
  ) => {
    setRowSelection({});
    setBulkIssueError(null);
    setExpectedDeliveryError(null);
    reanchorListNow();
    void navigate({
      from: '/purchase-orders/',
      to: '/purchase-orders',
      search: (previous) => patch(previous),
    });
  };

  const handleQueueChange = (nextQueue: PurchaseOrderWorkspaceQueue) => {
    navigateWorkspaceSearch((previous) => ({ ...previous, queue: nextQueue, ids: undefined }));
  };

  const handleQueueSelect = (nextQueue: PurchaseOrderWorkspaceQueue) => {
    const targetQueue = nextQueue === queue ? 'all' : nextQueue;
    const selectedStatusIds = poFilters.statusIds ?? [];
    const showableStatusIds = selectedStatusIds.filter((selectedStatusId) =>
      purchaseOrderQueueAdmitsStatus(targetQueue, selectedStatusId),
    );
    if (showableStatusIds.length !== selectedStatusIds.length) {
      applyPoFilters({ ...poFilters, statusIds: showableStatusIds });
    }
    handleQueueChange(targetQueue);
  };

  const handleStatusSelect = (nextStatusId: PurchaseOrderStatus) => {
    const current = poFilters.statusIds ?? [];
    const isDeselecting = current.includes(nextStatusId);
    applyPoFilters({
      ...poFilters,
      statusIds: isDeselecting
        ? current.filter((id) => id !== nextStatusId)
        : [...current, nextStatusId],
    });
    // The bar counts every status regardless of queue, so leave the queue only when it cannot hold the clicked status.
    if (!isDeselecting && !purchaseOrderQueueAdmitsStatus(queue, nextStatusId)) {
      handleQueueChange('all');
      return;
    }
    // The legend stays clickable over the just-created batch, which `ids` alone drives — so a click there must leave it.
    if (isRecentFilterActive) {
      navigateWorkspaceSearch((previous) => ({ ...previous, ids: undefined }));
    }
  };

  // The legend escapes a queue that can't hold the clicked status, so the toolbar's Status list must too.
  const handleToolbarFiltersChange = (next: PurchaseOrderListFilters) => {
    applyPoFilters(next);
    const hasUnadmittedStatus = (next.statusIds ?? []).some(
      (nextStatusId) => !purchaseOrderQueueAdmitsStatus(queue, nextStatusId),
    );
    if (hasUnadmittedStatus) handleQueueChange('all');
  };

  const handleBulkIssue = async () => {
    if (draftIds.length === 0) return;

    setBulkIssueError(null);
    setBulkIssuing(true);
    try {
      const { succeededNums, failures } = await issuePurchaseOrdersSequentially(
        draftIds,
        async (id) => {
          const { data: mutationData } = await issuePurchaseOrder({ variables: { id } });
          const issued = mutationData?.issuePurchaseOrder;
          if (!issued?.num) throw new Error(`Mutation returned no purchase order for ${id}`);
          return { num: issued.num };
        },
      );

      for (const num of succeededNums) {
        toastEntityUpdated(num, 'issued');
      }

      const issueFailureMessage =
        failures.length > 0
          ? `Could not issue: ${failures
              .map(
                (failure) => purchaseOrders.find((po) => po.id === failure.id)?.num ?? failure.id,
              )
              .join(', ')}.`
          : null;

      if (succeededNums.length > 0) {
        try {
          await Promise.all([refetch(), refetchMetrics()]);
        } catch {
          const suffix = issueFailureMessage !== null ? ` ${issueFailureMessage}` : '';
          setBulkIssueError({
            title:
              issueFailureMessage !== null ? BULK_ISSUE_FAILED_TITLE : LIST_NOT_REFRESHED_TITLE,
            description: `Purchase orders were issued, but the list could not be refreshed. Reload to see the latest state.${suffix}`,
          });
          return;
        }
      }

      if (issueFailureMessage !== null) {
        setBulkIssueError({ title: BULK_ISSUE_FAILED_TITLE, description: issueFailureMessage });
      }
    } finally {
      setBulkIssuing(false);
      setRowSelection({});
    }
  };

  const listTableProps = {
    ...tableProps,
    onNextPage: () => {
      setRowSelection({});
      tableProps.onNextPage();
    },
    onPreviousPage: () => {
      setRowSelection({});
      tableProps.onPreviousPage();
    },
    onSearchChange: (value: string) => {
      setRowSelection({});
      tableProps.onSearchChange(value);
    },
  };

  // Recent-ids mode is one fixed page of just-created POs, so paging, sorting, search and export are off.
  const recentFilterTableProps = {
    ...listTableProps,
    hasNextPage: false,
    hasPreviousPage: false,
    onFirstPage: undefined,
    onLastPage: undefined,
    pageIndex: 0,
    pageSize: recentIds.length,
    pageSizeOptions: undefined,
    onPageSizeChange: undefined,
    totalCount: recentIds.length,
    sortBy: undefined,
    onUpdateSortBy: undefined,
    manualSorting: false,
    onRequestExportRows: undefined,
    searchValue: undefined,
    onSearchChange: undefined,
  };

  return (
    <DimeCard borderless className="gap-0 py-0">
      <DimeCardHeader className="px-0 mb-4">
        <DimeCardTitle className="text-base flex items-center gap-2">Purchase orders</DimeCardTitle>
        <DimeCardDescription className="text-sm">
          Review and manage purchase orders created from part selections.
        </DimeCardDescription>
        <DimeCardAction>{createPoAction}</DimeCardAction>
      </DimeCardHeader>

      {isRecentFilterActive && (
        <DimeAlert className="mb-4">
          <DimeAlertTitle>Recently created purchase orders</DimeAlertTitle>
          <DimeAlertDescription className="flex flex-wrap items-center gap-x-2 gap-y-1">
            Showing {purchaseOrders.length} purchase order{purchaseOrders.length === 1 ? '' : 's'}{' '}
            from your latest batch create.
            <DimeButton
              type="button"
              variant="link"
              className="h-auto px-0"
              onClick={() => {
                navigateWorkspaceSearch((previous) => ({ ...previous, ids: undefined }));
              }}
            >
              Show all purchase orders
            </DimeButton>
          </DimeAlertDescription>
        </DimeAlert>
      )}

      {error && purchaseOrders.length === 0 ? (
        <ListErrorState
          title="Could not load purchase orders"
          message={error.message}
          onRetry={() => void refetch()}
        />
      ) : (
        <div className="min-w-0 space-y-3">
          {metricsError && !metricsData ? (
            <>
              <ListErrorState
                title="Could not load purchase order counts"
                message={metricsError.message}
                onRetry={() => void refetchMetrics()}
              />
              {queue !== 'all' && (
                <DimeAlert className="mb-0">
                  <DimeAlertDescription className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span>Showing {queueMetadata.label} only.</span>
                    <DimeButton
                      type="button"
                      variant="link"
                      className="h-auto px-0"
                      onClick={() => handleQueueChange('all')}
                    >
                      Show every purchase order
                    </DimeButton>
                  </DimeAlertDescription>
                </DimeAlert>
              )}
            </>
          ) : (
            <PurchaseOrderMetricsBar
              loading={metricsLoading && !metricsData}
              metrics={metrics}
              queue={queue}
              selectedStatusIds={poFilters.statusIds ?? []}
              onQueueSelect={handleQueueSelect}
              onStatusSelect={handleStatusSelect}
            />
          )}
          {expectedDeliveryError && (
            <DimeAlert variant="destructive" className="mb-0">
              <DimeAlertTitle>{expectedDeliveryError.title}</DimeAlertTitle>
              <DimeAlertDescription>{expectedDeliveryError.description}</DimeAlertDescription>
            </DimeAlert>
          )}
          {bulkIssueError && (
            <DimeAlert variant="destructive" className="mb-0">
              <DimeAlertTitle>{bulkIssueError.title}</DimeAlertTitle>
              <DimeAlertDescription>{bulkIssueError.description}</DimeAlertDescription>
            </DimeAlert>
          )}
          {showBulkIssueButton && (
            <div className="flex justify-end">
              <DimeButton
                type="button"
                disabled={bulkIssuing}
                onClick={() => {
                  void handleBulkIssue();
                }}
              >
                {bulkIssuing ? 'Issuing…' : 'Issue selected'}
              </DimeButton>
            </div>
          )}
          {showEmptyState ? (
            <ListEmptyState
              icon={<ShoppingCart />}
              title={queueMetadata.emptyTitle}
              description={queueMetadata.emptyDescription}
              action={queue === 'all' ? createPoAction : undefined}
            />
          ) : (
            <div className="overflow-x-auto">
              <DimeDataTable
                {...(isRecentFilterActive ? recentFilterTableProps : listTableProps)}
                columns={columns}
                data={purchaseOrders}
                maxBodyHeight="viewport"
                isLoading={loading}
                loadingRowCount={isRecentFilterActive ? recentIds.length : tableProps.pageSize}
                exportFileName="purchase-orders"
                filters={
                  isRecentFilterActive ? undefined : (
                    <PurchaseOrderToolbarFilters
                      filters={poFilters}
                      vendors={vendors}
                      vendorSearch={vendorSearch}
                      brands={brands}
                      warehouses={warehouses}
                      states={states}
                      onVendorSearch={setVendorSearch}
                      vendorSearchLoading={vendorsLoading}
                      vendorSearchError={vendorsError !== undefined}
                      onChange={handleToolbarFiltersChange}
                    />
                  )
                }
                searchColumn={isRecentFilterActive ? undefined : 'num'}
                searchPlaceholder={PO_SEARCH_PLACEHOLDER}
                showViewOptions
                showSelect={canEdit}
                selected={rowSelection}
                onUpdateSelected={setRowSelection}
                emptyMessage={
                  countPurchaseOrderFilters(poFilters) > 0 || search
                    ? 'No purchase orders found. Try adjusting your search or filters.'
                    : `${queueMetadata.emptyTitle}. ${queueMetadata.emptyDescription}`
                }
                onRowClick={(purchaseOrder) => {
                  void navigate({
                    to: '/purchase-orders/$purchaseOrderId',
                    params: { purchaseOrderId: purchaseOrder.id },
                    search: { edit: false },
                  });
                }}
              />
            </div>
          )}
        </div>
      )}
    </DimeCard>
  );
}
